import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MdCalendarToday,
  MdChevronRight,
  MdClass,
  MdRefresh,
  MdSchool,
  MdSearch,
  MdDateRange,
  MdSubject,
} from "react-icons/md";
import { API_BASE_URL } from "../../../utils/api";
import "./ViewTimetablePage.css";

const PERIODS_KEY = "als_timetable_periods";
const DAYS_KEY = "als_timetable_days";

const DEFAULT_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DEFAULT_PERIODS = [
  { id: "p1", type: "period", label: "Period 1", start_time: "08:00", end_time: "08:45" },
  { id: "p2", type: "period", label: "Period 2", start_time: "08:45", end_time: "09:30" },
  { id: "p3", type: "period", label: "Period 3", start_time: "09:30", end_time: "10:15" },
  { id: "p4", type: "period", label: "Period 4", start_time: "10:15", end_time: "11:00" },
  { id: "p5", type: "period", label: "Period 5", start_time: "11:00", end_time: "11:45" },
  { id: "break", type: "break", label: "Break", start_time: "11:45", end_time: "12:00" },
  { id: "p6", type: "period", label: "Period 6", start_time: "12:00", end_time: "12:45" },
  { id: "p7", type: "period", label: "Period 7", start_time: "12:45", end_time: "13:30" },
  { id: "p8", type: "period", label: "Period 8", start_time: "13:30", end_time: "14:15" },
];

function loadFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function normalizeTime(value) {
  if (!value) return "";
  const text = String(value);
  if (!text.includes(":")) return "";
  const [hours = "00", minutes = "00"] = text.split(":");
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
}

function formatTime12(value) {
  const time = normalizeTime(value);
  if (!time) return "";
  const [hStr, mStr] = time.split(":");
  let hour = parseInt(hStr, 10);
  if (Number.isNaN(hour)) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${mStr} ${ampm}`;
}

function formatRange(item) {
  if (!item?.start_time || !item?.end_time) return "";
  return `${formatTime12(item.start_time)} - ${formatTime12(item.end_time)}`;
}

function periodNumberFromId(periodId) {
  const parsed = parseInt(String(periodId).replace(/\D/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function classLabel(classItem) {
  if (!classItem) return "Select class";
  return classItem.section ? `${classItem.name} - ${classItem.section}` : classItem.name;
}

// ── Date range picker component ────────────────────────────────────────────
const DateRangePicker = ({ startDate, endDate, onStartChange, onEndChange }) => (
  <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
        From
      </span>
      <input
        type="date"
        value={startDate}
        onChange={(e) => onStartChange(e.target.value)}
        style={{
          padding: "8px 12px",
          borderRadius: "8px",
          border: "1.5px solid #e2e8f0",
          fontSize: "13px",
          color: "#1a202c",
          background: "#fff",
          outline: "none",
        }}
      />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
        To
      </span>
      <input
        type="date"
        value={endDate}
        onChange={(e) => onEndChange(e.target.value)}
        style={{
          padding: "8px 12px",
          borderRadius: "8px",
          border: "1.5px solid #e2e8f0",
          fontSize: "13px",
          color: "#1a202c",
          background: "#fff",
          outline: "none",
        }}
      />
    </div>
  </div>
);

// ── 3D Timetable View ──────────────────────────────────────────────────────
const Timetable3DView = ({ entries, classes, periods, days, classMap }) => {
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now);
    monday.setDate(diff);
    return monday.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? 0 : 7);
    const sunday = new Date(now);
    sunday.setDate(diff);
    return sunday.toISOString().split("T")[0];
  });

  // Generate dates between start and end
  const dateRange = useMemo(() => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, [startDate, endDate]);

  // Group entries by date and class
  const groupedData = useMemo(() => {
    const grouped = {};
    entries.forEach((entry) => {
      const dateKey = entry.date || entry.day_of_week;
      const classKey = entry.class_id || entry.class_name;
      if (!grouped[dateKey]) grouped[dateKey] = {};
      if (!grouped[dateKey][classKey]) grouped[dateKey][classKey] = [];
      grouped[dateKey][classKey].push(entry);
    });
    return grouped;
  }, [entries]);

  // Get day name from date
  const getDayName = (date) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[date.getDay()];
  };

  // Get week number
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  };

  // Get classes for a specific date
  const getClassesForDate = (dateKey) => {
    const dayName = getDayName(new Date(dateKey));
    // Filter entries by day of week
    const dayEntries = entries.filter(e => 
      (e.day_of_week || e.day) === dayName
    );
    const classIds = new Set(dayEntries.map(e => e.class_id || e.class_name));
    return Array.from(classIds);
  };

  if (entries.length === 0) {
    return (
      <div className="vt-no-data" style={{ padding: "40px", textAlign: "center" }}>
        No timetable entries found for the selected period.
      </div>
    );
  }

  return (
    <div>
      {/* Date Range Controls */}
      <div style={{ 
        padding: "16px 20px", 
        borderBottom: "1px solid #e2e8f0", 
        background: "#f8fafc",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <MdDateRange size={20} color="#64748b" />
          <span style={{ fontWeight: 700, fontSize: "13px", color: "#1a202c" }}>
            Week {dateRange.length > 0 ? getWeekNumber(dateRange[0]) : ""}
          </span>
        </div>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartChange={setStartDate}
          onEndChange={setEndDate}
        />
      </div>

      {/* 3D Table */}
      <div style={{ overflowX: "auto", padding: "16px" }}>
        <table style={{ 
          width: "100%", 
          borderCollapse: "collapse", 
          minWidth: "800px",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          overflow: "hidden"
        }}>
          <thead>
            <tr>
              <th style={{ 
                padding: "12px 16px", 
                background: "#0f1b2d", 
                color: "#fff",
                fontWeight: 700,
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                borderRight: "1px solid #1e2f56",
                minWidth: "100px"
              }}>
                Date / Class
              </th>
              {classes.map((cls) => (
                <th key={cls.id} style={{ 
                  padding: "12px 16px", 
                  background: "#0f1b2d", 
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderRight: "1px solid #1e2f56",
                  minWidth: "120px"
                }}>
                  {cls.section ? `${cls.name} - ${cls.section}` : cls.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dateRange.map((date, dateIndex) => {
              const dateStr = date.toISOString().split("T")[0];
              const dayName = getDayName(date);
              const dayEntries = entries.filter(e => 
                (e.day_of_week || e.day) === dayName
              );
              
              // Check if any entries for this day
              const hasEntries = dayEntries.length > 0;

              return (
                <tr key={dateStr} style={{ 
                  background: dateIndex % 2 === 0 ? "#ffffff" : "#f8fafc"
                }}>
                  <td style={{ 
                    padding: "12px 16px", 
                    borderBottom: "1px solid #e2e8f0",
                    fontWeight: 700,
                    fontSize: "13px",
                    color: "#1a202c",
                    borderRight: "1px solid #e2e8f0"
                  }}>
                    <div>{date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 400 }}>{dayName}</div>
                  </td>
                  {classes.map((cls) => {
                    const classEntries = dayEntries.filter(e => 
                      String(e.class_id) === String(cls.id) || e.class_name === cls.name
                    );
                    const subject = classEntries.length > 0 ? classEntries[0].course_name || classEntries[0].subject : null;
                    const teacher = classEntries.length > 0 ? classEntries[0].teacher_name : null;
                    const period = classEntries.length > 0 ? classEntries[0].period_number : null;

                    return (
                      <td key={`${dateStr}-${cls.id}`} style={{ 
                        padding: "12px 16px", 
                        borderBottom: "1px solid #e2e8f0",
                        borderRight: "1px solid #e2e8f0",
                        textAlign: "center",
                        verticalAlign: "middle"
                      }}>
                        {subject ? (
                          <div style={{ 
                            background: "#e8f0fe", 
                            borderRadius: "8px",
                            padding: "8px 12px",
                            border: "1px solid #c7d8f0"
                          }}>
                            <div style={{ fontWeight: 700, fontSize: "13px", color: "#1a202c" }}>
                              {subject}
                            </div>
                            {teacher && (
                              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                                {teacher}
                              </div>
                            )}
                            {period && (
                              <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "2px" }}>
                                Period {period}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ color: "#94a3b8", fontSize: "12px" }}>—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ 
        padding: "12px 20px", 
        borderTop: "1px solid #e2e8f0",
        background: "#f8fafc",
        display: "flex",
        gap: "20px",
        flexWrap: "wrap",
        fontSize: "12px",
        color: "#64748b"
      }}>
        <span>📚 <strong>{entries.length}</strong> total slots</span>
        <span>🏫 <strong>{classes.length}</strong> classes</span>
        <span>📅 <strong>{dateRange.length}</strong> days</span>
        <span style={{ color: "#94a3b8" }}>
          Click "Refresh" to update timetable data
        </span>
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────
function ViewTimetablePage({ onLogout, showToast, userRole }) {
  const token = localStorage.getItem("als_token");
  const isAdmin = userRole === "principal" || userRole === "admin" || userRole === "superadmin";

  const [campuses, setCampuses] = useState([]);
  const [campusClassesMap, setCampusClassesMap] = useState({});
  const [timetableByCampus, setTimetableByCampus] = useState({});
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyScheduled, setShowOnlyScheduled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const storageData = useMemo(
    () => ({
      periods: loadFromStorage(PERIODS_KEY, {}),
      days: loadFromStorage(DAYS_KEY, {}),
    }),
    [],
  );

  const fetchCampuses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/campuses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) throw new Error("Failed to fetch campuses");

      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setCampuses(list);
      setSelectedCampusId((current) => current || (list[0]?.id ? String(list[0].id) : ""));
    } catch (err) {
      console.error(err);
      showToast?.("error", "Load Failed", "Could not load campuses.");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout, showToast]);

  useEffect(() => {
    fetchCampuses();
  }, [fetchCampuses]);

  useEffect(() => {
    if (campuses.length === 0 || !token) return;

    const fetchCampusDetails = async () => {
      setDetailsLoading(true);
      try {
        const results = await Promise.all(
          campuses.map(async (campus) => {
            const [classesRes, timetableRes] = await Promise.all([
              fetch(`${API_BASE_URL}/api/classes?campus_id=${campus.id}`, {
                headers: { Authorization: `Bearer ${token}` },
              }),
              fetch(`${API_BASE_URL}/api/timetable?campus_id=${campus.id}`, {
                headers: { Authorization: `Bearer ${token}` },
              }),
            ]);

            if (classesRes.status === 401 || timetableRes.status === 401) {
              onLogout?.();
              return [campus.id, [], []];
            }

            const classes = classesRes.ok ? await classesRes.json() : [];
            const timetable = timetableRes.ok ? await timetableRes.json() : [];
            return [
              campus.id,
              Array.isArray(classes) ? classes : [],
              Array.isArray(timetable) ? timetable : [],
            ];
          }),
        );

        const classMap = {};
        const timetableMap = {};
        results.forEach(([campusId, classes, timetable]) => {
          classMap[campusId] = classes;
          timetableMap[campusId] = timetable;
        });
        setCampusClassesMap(classMap);
        setTimetableByCampus(timetableMap);
      } catch (err) {
        console.error(err);
        showToast?.("error", "Load Failed", "Could not load campus timetables.");
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchCampusDetails();
  }, [campuses, token, onLogout, showToast]);

  const getClassesForCampus = useCallback(
    (campusId) => campusClassesMap[String(campusId)] || [],
    [campusClassesMap],
  );

  const getTimetableForCampus = useCallback(
    (campusId) => timetableByCampus[String(campusId)] || [],
    [timetableByCampus],
  );

  const selectedCampus = useMemo(
    () => campuses.find((campus) => String(campus.id) === String(selectedCampusId)) || null,
    [campuses, selectedCampusId],
  );

  const selectedCampusClasses = useMemo(
    () => getClassesForCampus(selectedCampusId),
    [getClassesForCampus, selectedCampusId],
  );

  const selectedCampusTimetable = useMemo(
    () => getTimetableForCampus(selectedCampusId),
    [getTimetableForCampus, selectedCampusId],
  );

  useEffect(() => {
    if (!selectedCampusId) {
      setSelectedClassId("");
      return;
    }
    const classes = getClassesForCampus(selectedCampusId);
    const stillExists = classes.some((item) => String(item.id) === String(selectedClassId));
    if (!stillExists) {
      setSelectedClassId(classes[0]?.id ? String(classes[0].id) : "");
    }
  }, [getClassesForCampus, selectedCampusId, selectedClassId]);

  const selectedClass = useMemo(
    () => selectedCampusClasses.find((item) => String(item.id) === String(selectedClassId)) || null,
    [selectedCampusClasses, selectedClassId],
  );

  const selectedClassEntries = useMemo(
    () =>
      selectedCampusTimetable.filter(
        (entry) => String(entry.class_id) === String(selectedClassId),
      ),
    [selectedCampusTimetable, selectedClassId],
  );

  const campusPeriods = useMemo(() => {
    const saved = storageData.periods[String(selectedCampusId)];
    return Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_PERIODS;
  }, [selectedCampusId, storageData.periods]);

  const campusDays = useMemo(() => {
    const saved = storageData.days[String(selectedCampusId)];
    return Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_DAYS;
  }, [selectedCampusId, storageData.days]);

  const filteredCampuses = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return campuses;
    return campuses.filter((campus) =>
      String(campus.name || "").toLowerCase().includes(query),
    );
  }, [campuses, searchTerm]);

  const summary = useMemo(() => {
    let totalClasses = 0;
    let totalSlots = 0;
    let campusesWithTimetable = 0;

    campuses.forEach((campus) => {
      const classes = getClassesForCampus(campus.id);
      const entries = getTimetableForCampus(campus.id);
      totalClasses += classes.length;
      totalSlots += entries.length;
      if (entries.length > 0) campusesWithTimetable += 1;
    });

    return {
      campuses: campuses.length,
      totalClasses,
      totalSlots,
      campusesWithTimetable,
    };
  }, [campuses, getClassesForCampus, getTimetableForCampus]);

  const getCampusStats = (campusId) => {
    const classes = getClassesForCampus(campusId);
    const entries = getTimetableForCampus(campusId);
    const scheduledClassIds = new Set(entries.map((entry) => String(entry.class_id)));
    return {
      classes: classes.length,
      slots: entries.length,
      scheduledClasses: classes.filter((item) => scheduledClassIds.has(String(item.id))).length,
    };
  };

  const getEntry = (day, periodId) => {
    const periodNumber = periodNumberFromId(periodId);
    return selectedClassEntries.find(
      (entry) =>
        (entry.day_of_week || entry.day) === day &&
        Number(entry.period_number) === periodNumber,
    );
  };

  const sortedEntries = useMemo(() => {
    return [...selectedClassEntries].sort((a, b) => {
      const dayDiff = campusDays.indexOf(a.day_of_week) - campusDays.indexOf(b.day_of_week);
      if (dayDiff !== 0) return dayDiff;
      return Number(a.period_number || 0) - Number(b.period_number || 0);
    });
  }, [campusDays, selectedClassEntries]);

  const handleSelectCampus = (campusId) => {
    setSelectedCampusId(String(campusId));
    setSelectedClassId("");
  };

  const singleCampus = campuses.length === 1;

  if (loading) {
    return <div className="vt-page-loading">Loading timetable data...</div>;
  }

  return (
    <div className="vt-page">
      <div className="vt-header">
        <div>
          <h2>View Timetable</h2>
          <p>
            {isAdmin 
              ? "3D View: Browse timetable by Date, Class, and Subject"
              : singleCampus
                ? `Timetable for ${campuses[0]?.name || "your campus"}.`
                : "Select a campus card to open its timetable."
            }
          </p>
        </div>
        <button className="vt-refresh-btn" onClick={fetchCampuses} type="button">
          <MdRefresh /> Refresh
        </button>
      </div>

      <div className="vt-stats-bar">
        {!singleCampus && (
          <div className="vt-stat-card">
            <span className="vt-stat-number">{summary.campuses}</span>
            <span className="vt-stat-label">Campuses</span>
          </div>
        )}
        <div className="vt-stat-card">
          <span className="vt-stat-number">{summary.totalClasses}</span>
          <span className="vt-stat-label">Classes</span>
        </div>
        <div className="vt-stat-card vt-stat-highlight">
          <span className="vt-stat-number">{summary.campusesWithTimetable}</span>
          <span className="vt-stat-label">With Timetable</span>
        </div>
        <div className="vt-stat-card">
          <span className="vt-stat-number">{summary.totalSlots}</span>
          <span className="vt-stat-label">Slots Loaded</span>
        </div>
      </div>

      {!singleCampus && !isAdmin && (
        <div className="vt-controls-bar">
          <label className="vt-search-wrap">
            <MdSearch />
            <input
              className="vt-search-input"
              type="search"
              placeholder="Search campus"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
          <label className="vt-toggle-label">
            <input
              type="checkbox"
              checked={showOnlyScheduled}
              onChange={(event) => setShowOnlyScheduled(event.target.checked)}
            />
            Show campuses with timetable only
          </label>
        </div>
      )}

      {!singleCampus && !isAdmin && (
        <div className="vt-campus-grid">
          {filteredCampuses
            .filter((campus) => {
              if (!showOnlyScheduled) return true;
              return getCampusStats(campus.id).slots > 0;
            })
            .map((campus) => {
              const stats = getCampusStats(campus.id);
              const active = String(campus.id) === String(selectedCampusId);
              return (
                <button
                  key={campus.id}
                  type="button"
                  className={`vt-campus-card ${active ? "active" : ""}`}
                  onClick={() => handleSelectCampus(campus.id)}
                >
                  <span className="vt-campus-card-top">
                    <span className="vt-campus-icon"><MdSchool /></span>
                    <span className="vt-campus-open">
                      Open <MdChevronRight />
                    </span>
                  </span>
                  <span className="vt-campus-name">{campus.name}</span>
                  <span className="vt-campus-meta">
                    {stats.scheduledClasses} of {stats.classes} classes scheduled
                  </span>
                  <span className="vt-campus-card-stats">
                    <span>
                      <strong>{stats.classes}</strong>
                      Classes
                    </span>
                    <span>
                      <strong>{stats.slots}</strong>
                      Slots
                    </span>
                  </span>
                </button>
              );
            })}
        </div>
      )}

      {!singleCampus && !isAdmin && filteredCampuses.length === 0 && (
        <div className="vt-empty">
          <MdSchool />
          <h3>No campuses found</h3>
          <p>Try a different search.</p>
        </div>
      )}

      <section className="vt-detail-panel">
        <div className="vt-detail-header">
          <div>
            <span className="vt-eyebrow">{isAdmin ? "📊 3D Timetable View" : "Opened Campus"}</span>
            <h3>{isAdmin ? "Date × Class × Subject" : (selectedCampus?.name || "Select a campus")}</h3>
            <p>
              {isAdmin 
                ? "View all class schedules organized by date, class, and subject."
                : selectedCampus
                  ? "Choose a class to view its timetable table."
                  : "Pick one campus card above to open the timetable."
              }
            </p>
          </div>
          {!isAdmin && (
            <div className="vt-detail-actions">
              <label>
                <span>Class</span>
                <select
                  value={selectedClassId}
                  onChange={(event) => setSelectedClassId(event.target.value)}
                  disabled={!selectedCampus || selectedCampusClasses.length === 0}
                >
                  {selectedCampusClasses.length === 0 ? (
                    <option value="">No classes</option>
                  ) : (
                    selectedCampusClasses.map((classItem) => (
                      <option key={classItem.id} value={classItem.id}>
                        {classLabel(classItem)}
                      </option>
                    ))
                  )}
                </select>
              </label>
            </div>
          )}
        </div>

        {detailsLoading ? (
          <div className="vt-no-data">Loading campus timetable...</div>
        ) : !isAdmin && !selectedCampus ? (
          <div className="vt-no-data">Select a campus to view timetable details.</div>
        ) : !isAdmin && selectedCampusClasses.length === 0 ? (
          <div className="vt-no-data">No classes have been created for this campus yet.</div>
        ) : !isAdmin && !selectedClass ? (
          <div className="vt-no-data">Select a class to view its timetable.</div>
        ) : isAdmin ? (
          // ── Admin 3D View ──────────────────────────────────────────────────
          <Timetable3DView
            entries={selectedCampusTimetable}
            classes={selectedCampusClasses}
            periods={campusPeriods}
            days={campusDays}
            classMap={campusClassesMap}
          />
        ) : (
          // ── Teacher / Regular View ──────────────────────────────────────
          <>
            <div className="vt-class-summary">
              <span><MdClass /> {classLabel(selectedClass)}</span>
              <span><MdCalendarToday /> {selectedClassEntries.length} scheduled slots</span>
            </div>

            <div className="vt-grid-wrapper">
              <table className="vt-grid">
                <thead>
                  <tr>
                    <th className="vt-col-period">Period</th>
                    {campusDays.map((day) => (
                      <th key={day}>{day.slice(0, 3).toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {campusPeriods.map((period, rowIndex) =>
                    period.type === "break" ? (
                      <tr key={period.id} className="vt-break-row">
                        <td className="vt-period-label">
                          <div className="vt-period-name">{period.label}</div>
                          <div className="vt-period-time">{formatRange(period)}</div>
                        </td>
                        <td colSpan={campusDays.length} className="vt-break-cell">
                          <span className="vt-break-pill">Break - {formatRange(period)}</span>
                        </td>
                      </tr>
                    ) : (
                      <tr key={period.id} className={rowIndex % 2 === 0 ? "vt-row-even" : "vt-row-odd"}>
                        <td className="vt-period-label">
                          <div className="vt-period-name">{period.label}</div>
                          <div className="vt-period-time">{formatRange(period)}</div>
                        </td>
                        {campusDays.map((day) => {
                          const entry = getEntry(day, period.id);
                          return (
                            <td key={day} className="vt-cell" data-day={day.slice(0, 3).toUpperCase()}>
                              {entry ? (
                                <div className="vt-slot-filled">
                                  <div className="vt-slot-course">{entry.course_name || "Course"}</div>
                                  <div className="vt-slot-teacher">{entry.teacher_name || "Teacher"}</div>
                                </div>
                              ) : (
                                <div className="vt-slot-empty">-</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>

            <div className="vt-list-table-wrap">
              <table className="vt-list-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Period</th>
                    <th>Time</th>
                    <th>Course</th>
                    <th>Teacher</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="vt-table-empty">
                        No timetable slots have been scheduled for this class.
                      </td>
                    </tr>
                  ) : (
                    sortedEntries.map((entry) => {
                      const period = campusPeriods.find(
                        (item) => periodNumberFromId(item.id) === Number(entry.period_number),
                      );
                      return (
                        <tr key={entry.id}>
                          <td>{entry.day_of_week}</td>
                          <td>{period?.label || `Period ${entry.period_number}`}</td>
                          <td>{formatRange(entry) || formatRange(period)}</td>
                          <td>{entry.course_name || "Course"}</td>
                          <td>{entry.teacher_name || "Teacher"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default ViewTimetablePage;